'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from 'react';
import { cx } from 'class-variance-authority';
import { Button } from '~/components/primitives';
import type { ClassValue } from 'class-variance-authority/types';
import { usePortalCtx } from '~/features/global/portal';
import { backgroundColorVariant, textColorVariant, type BackgroundColorVariant } from '~/utils/variants';
const LEFT_PAGE = 'LEFT';
const RIGHT_PAGE = 'RIGHT';
type NumberedPaginationProps = {
  lastPage: number;
  /** The current nth page */
  current: number;
  previousText: string;
  nextText: string;
  /** @default 2 */
  pageNeighbours?: number;
  onChangePage?: (newPage: number) => void;
  className?: ClassValue;
  backgroundColor?: BackgroundColorVariant;
};
function range(from: number, to: number, step: number = 1) {
  let i = from;
  const rangeArray = [];
  while (i <= to) {
    rangeArray.push(i);
    i += step;
  }
  return rangeArray;
}
export default function NumberedPagination(t0) {
  const $ = _c(70);
  const {
    lastPage,
    current,
    pageNeighbours: t1,
    onChangePage,
    previousText,
    nextText,
    className,
    backgroundColor
  } = t0;
  const pageNeighbours = t1 === undefined ? 2 : t1;
  const portal = usePortalCtx();
  const [currentPage, setCurrentPage] = useState(current);
  if (currentPage > lastPage && lastPage !== 0) {
    setCurrentPage(1);
  }
  let t2;
  if ($[0] !== onChangePage) {
    t2 = number => {
      setCurrentPage(number);
      onChangePage?.(number);
    };
    $[0] = onChangePage;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleChangePage = t2;
  let t3;
  let t4;
  if ($[2] !== current) {
    t3 = () => {
      setCurrentPage(current);
    };
    t4 = [current];
    $[2] = current;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  bb0: {
    const totalNumbers = pageNeighbours * 2;
    const totalBlocks = totalNumbers + 2;
    if (lastPage > totalBlocks) {
      const startPage = Math.max(2, currentPage - pageNeighbours);
      const endPage = Math.min(lastPage - 1, currentPage + pageNeighbours);
      let pages;
      if ($[5] !== endPage || $[6] !== lastPage || $[7] !== startPage || $[8] !== totalNumbers) {
        pages = range(startPage, endPage);
        const hasLeftSpill = startPage > 2;
        const hasRightSpill = lastPage - endPage > 1;
        const spillOffset = totalNumbers - (pages.length + 1);
        bb1: switch (true) {
          case hasLeftSpill && !hasRightSpill:
            {
              const extraPages_0 = range(startPage - spillOffset, startPage - 1);
              pages = [LEFT_PAGE, ...extraPages_0, ...pages];
              break bb1;
            }
          case !hasLeftSpill && hasRightSpill:
            {
              const extraPages = range(endPage + 1, endPage + spillOffset);
              pages = [...pages, ...extraPages, RIGHT_PAGE];
              break bb1;
            }
          case hasLeftSpill && hasRightSpill:
          default:
            {
              pages = [LEFT_PAGE, ...pages, RIGHT_PAGE];
            }
        }
        $[5] = endPage;
        $[6] = lastPage;
        $[7] = startPage;
        $[8] = totalNumbers;
        $[9] = pages;
      } else {
        pages = $[9];
      }
      let t6;
      if ($[10] !== lastPage || $[11] !== pages) {
        t6 = [1, ...pages, lastPage];
        $[10] = lastPage;
        $[11] = pages;
        $[12] = t6;
      } else {
        t6 = $[12];
      }
      t5 = t6;
      break bb0;
    }
    let t6;
    if ($[13] !== lastPage) {
      t6 = range(1, lastPage);
      $[13] = lastPage;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    t5 = t6;
  }
  const pageNumbers = t5;
  let t6;
  bb2: {
    if (lastPage <= 3) {
      t6 = pageNumbers;
      break bb2;
    }
    let mobilePages;
    if ($[15] !== currentPage || $[16] !== lastPage) {
      mobilePages = [];
      mobilePages.push(1);
      if (currentPage > 2) {
        if (currentPage > 3) {
          mobilePages.push(LEFT_PAGE);
        }
      }
      if (currentPage !== 1 && currentPage !== lastPage) {
        mobilePages.push(currentPage);
      }
      if (currentPage < lastPage - 1) {
        if (currentPage < lastPage - 2) {
          mobilePages.push(RIGHT_PAGE);
        }
      }
      if (lastPage !== 1) {
        mobilePages.push(lastPage);
      }
      $[15] = currentPage;
      $[16] = lastPage;
      $[17] = mobilePages;
    } else {
      mobilePages = $[17];
    }
    t6 = mobilePages;
  }
  const mobilePageNumbers = t6;
  if (pageNumbers.length === 0) {
    return null;
  }
  let t7;
  if ($[18] !== backgroundColor || $[19] !== className || $[20] !== portal) {
    t7 = cx("flex items-start justify-between gap-1 py-2", backgroundColor ? backgroundColorVariant({
      backgroundColor
    }) : portal.portal === "WEB" && "bg-brown-50", className);
    $[18] = backgroundColor;
    $[19] = className;
    $[20] = portal;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const t8 = currentPage === 1;
  let t9;
  if ($[22] !== currentPage || $[23] !== handleChangePage) {
    t9 = e => {
      e.preventDefault();
      handleChangePage(currentPage > 1 ? currentPage - 1 : 1);
    };
    $[22] = currentPage;
    $[23] = handleChangePage;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== previousText) {
    t10 = <span className="hidden sm:block">{previousText}</span>;
    $[25] = previousText;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t8 || $[29] !== t9) {
    t11 = <Button iconBefore="chevron-left" disabled={t8} onClick={t9} className="py-2.5! max-sm:px-2.5" variant="secondary">{t10}</Button>;
    $[27] = t10;
    $[28] = t8;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== backgroundColor || $[32] !== currentPage || $[33] !== handleChangePage || $[34] !== mobilePageNumbers) {
    let t13;
    if ($[36] !== backgroundColor || $[37] !== currentPage || $[38] !== handleChangePage) {
      t13 = page => {
        const isActive = currentPage === page;
        if (page === LEFT_PAGE) {
          return <span key={page} className={cx("px-4", backgroundColor === "blue-950" ? "text-white" : "text-brown-700")}>...</span>;
        }
        if (page === RIGHT_PAGE) {
          return <span key={page} className={cx("px-4", backgroundColor === "blue-950" ? "text-white" : "text-brown-700")}>...</span>;
        }
        return <button className={cx("h-11 w-11 rounded-full text-center text-base font-bold leading-none transition hover:bg-brown-200 hover:text-orange-500", isActive ? "bg-secondary text-white" : backgroundColor === "blue-950" ? textColorVariant({
          textColor: "white"
        }) : textColorVariant({
          textColor: "primary"
        }))} type="button" key={page} onClick={() => handleChangePage(page)}>{page}</button>;
      };
      $[36] = backgroundColor;
      $[37] = currentPage;
      $[38] = handleChangePage;
      $[39] = t13;
    } else {
      t13 = $[39];
    }
    t12 = mobilePageNumbers.map(t13);
    $[31] = backgroundColor;
    $[32] = currentPage;
    $[33] = handleChangePage;
    $[34] = mobilePageNumbers;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[40] !== t12) {
    t13 = <div className="block sm:hidden">{t12}</div>;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== backgroundColor || $[43] !== currentPage || $[44] !== handleChangePage || $[45] !== pageNumbers) {
    let t15;
    if ($[47] !== backgroundColor || $[48] !== currentPage || $[49] !== handleChangePage) {
      t15 = page_0 => {
        const isActive_0 = currentPage === page_0;
        if (page_0 === LEFT_PAGE) {
          return <span key={page_0} className={cx("px-4", backgroundColor === "blue-950" ? "text-white" : "text-brown-700")}>...</span>;
        }
        if (page_0 === RIGHT_PAGE) {
          return <span key={page_0} className={cx("px-4", backgroundColor === "blue-950" ? "text-white" : "text-brown-700")}>...</span>;
        }
        return <button className={cx("h-11 w-11 rounded-full text-center text-base font-bold leading-none transition hover:bg-brown-200 hover:text-orange-500", isActive_0 ? "bg-secondary text-white" : backgroundColor === "blue-950" ? textColorVariant({
          textColor: "white"
        }) : textColorVariant({
          textColor: "primary"
        }))} type="button" key={page_0} onClick={() => handleChangePage(page_0)}>{page_0}</button>;
      };
      $[47] = backgroundColor;
      $[48] = currentPage;
      $[49] = handleChangePage;
      $[50] = t15;
    } else {
      t15 = $[50];
    }
    t14 = pageNumbers.map(t15);
    $[42] = backgroundColor;
    $[43] = currentPage;
    $[44] = handleChangePage;
    $[45] = pageNumbers;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[51] !== t14) {
    t15 = <div className="hidden sm:block">{t14}</div>;
    $[51] = t14;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== t13 || $[54] !== t15) {
    t16 = <div className="flex flex-1 items-center justify-center sm:gap-2">{t13}{t15}</div>;
    $[53] = t13;
    $[54] = t15;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  const t17 = currentPage === lastPage;
  let t18;
  if ($[56] !== currentPage || $[57] !== handleChangePage) {
    t18 = e_0 => {
      e_0.preventDefault();
      handleChangePage(currentPage + 1);
    };
    $[56] = currentPage;
    $[57] = handleChangePage;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== nextText) {
    t19 = <span className="hidden sm:block">{nextText}</span>;
    $[59] = nextText;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  let t20;
  if ($[61] !== t17 || $[62] !== t18 || $[63] !== t19) {
    t20 = <Button iconAfter="chevron-right" size="md" disabled={t17} onClick={t18} className="py-2.5! max-sm:px-2.5" variant="secondary">{t19}</Button>;
    $[61] = t17;
    $[62] = t18;
    $[63] = t19;
    $[64] = t20;
  } else {
    t20 = $[64];
  }
  let t21;
  if ($[65] !== t11 || $[66] !== t16 || $[67] !== t20 || $[68] !== t7) {
    t21 = <menu className={t7}>{t11}{t16}{t20}</menu>;
    $[65] = t11;
    $[66] = t16;
    $[67] = t20;
    $[68] = t7;
    $[69] = t21;
  } else {
    t21 = $[69];
  }
  return t21;
}

/** @param [pageSize=10] defaults to 10 */
NumberedPagination.useState = function useNumberedPaginationState(totalItems: number, pageSize = 10) {
  const [currentNthPage, setCurrentNthPage] = useState(1);
  const totalPages = Math.floor(totalItems / pageSize);
  return {
    /** Set the current nth page */
    setCurrent: setCurrentNthPage,
    totalPages,
    /** The current nth page */
    current: currentNthPage,
    lastPage: totalPages,
    onChangePage: setCurrentNthPage,
    pageBeginIndex: (currentNthPage - 1) * pageSize,
    pageEndIndex: currentNthPage * pageSize,
    pageSize
  } as const satisfies Partial<React.ComponentProps<typeof NumberedPagination>> & Record<string, unknown>;
};